import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatNumber, progressColor, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Loader2, TrendingUp, FileSpreadsheet, FileText, ArrowRight, Construction, Waves, CloudRain, Droplets, Zap, Cable, Container, Plus, Trash2, Sparkles, Lightbulb, Hammer, Truck, Home, Wrench, Package, Layers, Factory, Building2, Percent, Pencil, Check, X, Settings } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell } from "recharts";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const iconMap = {
  road: Construction,
  waves: Waves,
  "cloud-rain": CloudRain,
  droplets: Droplets,
  pipe: Cable,
  cable: Cable,
  zap: Zap,
  container: Container,
  lightbulb: Lightbulb,
  hammer: Hammer,
  truck: Truck,
  home: Home,
  wrench: Wrench,
  package: Package,
  layers: Layers,
  factory: Factory,
  building: Building2,
};

const ICON_OPTIONS = [
  { key: "package", Icon: Package, label: "Genel" },
  { key: "building", Icon: Building2, label: "Bina" },
  { key: "factory", Icon: Factory, label: "Fabrika" },
  { key: "hammer", Icon: Hammer, label: "Yapı" },
  { key: "wrench", Icon: Wrench, label: "Ekipman" },
  { key: "lightbulb", Icon: Lightbulb, label: "Aydınlatma" },
  { key: "truck", Icon: Truck, label: "Nakliye" },
  { key: "layers", Icon: Layers, label: "Katman" },
  { key: "home", Icon: Home, label: "Konut" },
  { key: "zap", Icon: Zap, label: "Enerji" },
];

const COLOR_OPTIONS = [
  "#0ea5e9", "#3b82f6", "#6366f1", "#8b5cf6",
  "#ec4899", "#ef4444", "#f97316", "#eab308",
  "#22c55e", "#14b8a6", "#64748b",
];

function ProgressBar({ pct }) {
  const color = progressColor(pct);
  return (
    <div className="w-full h-2 bg-slate-200 rounded-full overflow-hidden">
      <div
        style={{ width: `${Math.min(pct, 100)}%`, backgroundColor: color }}
        className="h-full transition-all"
      />
    </div>
  );
}

function ItemCard({ item, onClick, onDelete, onSavedPursantaj, isAdmin }) {
  const Icon = iconMap[item.icon] || Construction;
  const color = progressColor(item.progress);
  const groupCount = (item.groups || []).length;
  const [editingPursantaj, setEditingPursantaj] = useState(false);
  const [pursantajValue, setPursantajValue] = useState(String(item.pursantaj || 0));
  const [savingPursantaj, setSavingPursantaj] = useState(false);

  const savePursantaj = async (e) => {
    e.stopPropagation();
    const n = parseFloat(pursantajValue);
    if (isNaN(n) || n < 0 || n > 100) { toast.error("Pursantaj 0-100 arası olmalı"); return; }
    setSavingPursantaj(true);
    try {
      await api.put(`/items/${item.key}/pursantaj`, { pursantaj: n });
      toast.success("Pursantaj güncellendi");
      setEditingPursantaj(false);
      onSavedPursantaj();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setSavingPursantaj(false); }
  };

  return (
    <div
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") onClick(); }}
      className="site-card text-left p-5 rounded-lg group hover:shadow-lg active:scale-[0.99] cursor-pointer relative"
      data-testid={`card-${item.key}`}
    >
      <div className="flex items-start justify-between mb-4">
        <div
          className="w-11 h-11 rounded-md flex items-center justify-center text-white"
          style={{ backgroundColor: item.color }}
        >
          <Icon size={20} />
        </div>
        <div className="text-right">
          <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">İlerleme</div>
          <div className="font-display font-black text-2xl tabular-nums" style={{ color }} data-testid={`card-${item.key}-progress`}>
            %{item.progress.toFixed(1)}
          </div>
        </div>
      </div>

      <h3 className="font-display font-black text-lg tracking-tight text-slate-900 mb-1 flex items-center gap-2 flex-wrap">
        {item.name}
        {item.is_custom && (
          <span className="text-[9px] font-bold uppercase tracking-widest bg-sky-100 text-sky-700 border border-sky-200 px-1.5 py-0.5 rounded" title="Özel">
            <Sparkles size={9} className="inline mr-0.5 -mt-0.5" /> Özel
          </span>
        )}
      </h3>
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        {groupCount > 0 && (
          <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
            {groupCount} grup
          </div>
        )}
        {/* Pursantaj badge */}
        <div
          className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest bg-amber-50 text-amber-800 border border-amber-200 px-1.5 py-0.5 rounded-full"
          onClick={(e) => e.stopPropagation()}
          title="Bu imalatın proje içindeki pursantaj ağırlığı"
          data-testid={`pursantaj-${item.key}`}
        >
          <Percent size={9} />
          {editingPursantaj ? (
            <div className="flex items-center gap-1">
              <input
                type="number" step="0.1" min="0" max="100"
                value={pursantajValue}
                onChange={(e) => setPursantajValue(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") savePursantaj(e); if (e.key === "Escape") { setEditingPursantaj(false); setPursantajValue(String(item.pursantaj || 0)); } }}
                autoFocus
                className="w-14 h-5 text-[11px] px-1 border border-amber-400 rounded font-mono-num text-amber-900 bg-white"
                data-testid={`pursantaj-input-${item.key}`}
              />
              <button onClick={savePursantaj} disabled={savingPursantaj} className="text-green-700 hover:text-green-800">
                {savingPursantaj ? <Loader2 className="animate-spin" size={11} /> : <Check size={11} />}
              </button>
              <button onClick={(e) => { e.stopPropagation(); setEditingPursantaj(false); setPursantajValue(String(item.pursantaj || 0)); }} className="text-red-700 hover:text-red-800">
                <X size={11} />
              </button>
            </div>
          ) : (
            <>
              <span className="font-mono-num">Pursantaj: %{(item.pursantaj || 0).toFixed(1)}</span>
              {isAdmin && (
                <button
                  onClick={(e) => { e.stopPropagation(); setPursantajValue(String(item.pursantaj || 0)); setEditingPursantaj(true); }}
                  className="text-amber-700 hover:text-amber-900 opacity-60 hover:opacity-100"
                  title="Pursantaj düzenle"
                  data-testid={`pursantaj-edit-${item.key}`}
                >
                  <Pencil size={10} />
                </button>
              )}
            </>
          )}
        </div>
      </div>

      <ProgressBar pct={item.progress} />

      <div className="grid grid-cols-3 gap-2 mt-4">
        <div>
          <div className="text-[9px] font-bold uppercase tracking-wider text-slate-500">Planlanan</div>
          <div className="font-mono-num font-bold text-sm text-slate-900">{formatNumber(item.planned)}</div>
        </div>
        <div>
          <div className="text-[9px] font-bold uppercase tracking-wider text-slate-500">Yapılan</div>
          <div className="font-mono-num font-bold text-sm text-green-700">{formatNumber(item.done)}</div>
        </div>
        <div>
          <div className="text-[9px] font-bold uppercase tracking-wider text-slate-500">Kalan</div>
          <div className="font-mono-num font-bold text-sm text-sky-600">{formatNumber(item.remaining)}</div>
        </div>
      </div>

      <div className="mt-4 pt-3 border-t border-slate-200 flex items-center justify-between text-xs text-slate-500 group-hover:text-sky-500 font-semibold">
        <span>Alt kırılımları gör</span>
        <ArrowRight size={14} />
      </div>

      {isAdmin && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onDelete(item); }}
          className="absolute top-3 right-3 p-1.5 rounded-md text-red-600 opacity-40 hover:opacity-100 hover:bg-red-50"
          title="İmalatı sil"
          data-testid={`delete-item-${item.key}`}
        >
          <Trash2 size={14} />
        </button>
      )}
    </div>
  );
}

function UnitProgressCard() { return null; /* deprecated: birim bazlı özet kaldırıldı */ }

export default function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [overallProgress, setOverallProgress] = useState(0);
  const [totalWeight, setTotalWeight] = useState(0);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);
  const [showAddItem, setShowAddItem] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);
  const [showReportSettings, setShowReportSettings] = useState(false);
  const isAdmin = user?.role === "admin";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/dashboard/summary");
      setItems(data.items || []);
      setOverallProgress(data.overall_progress || 0);
      setTotalWeight(data.total_weight || 0);
    } catch (e) {
      toast.error("Veriler yüklenemedi");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const onDeleteItem = async () => {
    if (!deleteItem) return;
    try {
      await api.delete(`/items/${deleteItem.key}`);
      toast.success("İmalat silindi");
      setDeleteItem(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const downloadReport = async (type) => {
    setDownloading(type);
    try {
      const res = await api.get(`/export/${type}`, { responseType: "blob" });
      const blob = new Blob([res.data]);
      const ext = type === "excel" ? "xlsx" : "pdf";
      const fname = `santiye_imalat_raporu_${new Date().toISOString().slice(0, 10)}.${ext}`;

      if (window.showSaveFilePicker) {
        try {
          const handle = await window.showSaveFilePicker({
            suggestedName: fname,
            types: [
              type === "excel"
                ? { description: "Excel Çalışma Kitabı", accept: { "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"] } }
                : { description: "PDF Belgesi", accept: { "application/pdf": [".pdf"] } },
            ],
          });
          const writable = await handle.createWritable();
          await writable.write(blob);
          await writable.close();
          toast.success(`${type === "excel" ? "Excel" : "PDF"} kaydedildi`);
          return;
        } catch (err) {
          if (err?.name === "AbortError") return;
        }
      }

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fname;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(
        `${type === "excel" ? "Excel" : "PDF"} indirildi. Masaüstüne kaydetmek için tarayıcı ayarlarından "Her indirmede konum sor" seçeneğini açın.`
      );
    } catch (e) {
      toast.error("Rapor indirilemedi");
    } finally {
      setDownloading(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="animate-spin text-sky-500" size={32} />
      </div>
    );
  }

  const chartData = items.map((it) => ({
    name: it.name.length > 14 ? it.name.slice(0, 12) + "…" : it.name,
    fullName: it.name,
    progress: it.progress,
    color: progressColor(it.progress),
  }));

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-sky-500 mb-2">Ana Panel</div>
          <h1 className="text-3xl sm:text-4xl font-black text-slate-900 tracking-tight font-display">
            İmalat İlerleme Özeti
          </h1>
          <p className="text-slate-600 mt-2">
            Tüm imalat kalemlerinin planlanan, yapılan ve kalan metraj durumu — pursantaj ağırlıklı genel ilerleme ile.
          </p>
        </div>

        <div className="flex gap-2 flex-wrap">
          {isAdmin && (
            <Button
              variant="outline"
              className="border-2 border-slate-300 font-semibold h-11"
              onClick={() => setShowReportSettings(true)}
              data-testid="report-settings-button"
              title="Rapor için proje adı ve hazırlayan bilgisini ayarla"
            >
              <Settings size={16} className="mr-2" />
              Rapor Ayarları
            </Button>
          )}
          <Button
            variant="outline"
            className="border-2 border-slate-300 font-semibold h-11"
            onClick={() => downloadReport("excel")}
            disabled={downloading !== null}
            data-testid="export-excel-button"
          >
            {downloading === "excel" ? <Loader2 className="animate-spin" size={16} /> : <FileSpreadsheet size={16} className="mr-2" />}
            Excel
          </Button>
          <Button
            variant="outline"
            className="border-2 border-slate-300 font-semibold h-11"
            onClick={() => downloadReport("pdf")}
            disabled={downloading !== null}
            data-testid="export-pdf-button"
          >
            {downloading === "pdf" ? <Loader2 className="animate-spin" size={16} /> : <FileText size={16} className="mr-2" />}
            PDF
          </Button>
        </div>
      </div>

      {/* Overall stat strip */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <div className="site-card p-5 rounded-lg" data-testid="stat-total-items">
          <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Toplam İmalat Kalemi</div>
          <div className="font-display font-black text-3xl text-slate-900 mt-2">{items.length}</div>
          <div className="text-xs text-slate-500 mt-1">Ana kalem takibi</div>
        </div>
        <div className="site-card p-5 rounded-lg" data-testid="stat-overall-progress">
          <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Genel Ortalama İlerleme</div>
          <div className="font-display font-black text-3xl mt-2" style={{ color: progressColor(overallProgress) }}>
            %{overallProgress.toFixed(1)}
          </div>
          <div className="mt-2"><ProgressBar pct={overallProgress} /></div>
          <div className="text-[10px] text-slate-500 mt-1">
            {totalWeight > 0
              ? <>Pursantaj ağırlıklı · Toplam ağırlık: <span className="font-mono-num font-bold text-amber-700">%{totalWeight.toFixed(1)}</span></>
              : "Pursantaj tanımlı değil — basit ortalama"}
          </div>
        </div>
        <div className="site-card p-5 rounded-lg" data-testid="stat-weight-info">
          <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 flex items-center gap-1">
            <Percent size={11} className="text-amber-600" /> Pursantaj Toplamı
          </div>
          <div className="font-display font-black text-3xl mt-2 tabular-nums" style={{ color: Math.abs(totalWeight - 100) < 0.1 ? "#059669" : totalWeight > 0 ? "#d97706" : "#94a3b8" }}>
            %{totalWeight.toFixed(1)}
          </div>
          <div className="text-[10px] text-slate-500 mt-1">
            {Math.abs(totalWeight - 100) < 0.1 ? "✓ Toplam %100" : totalWeight > 0 ? "Toplam %100 olmalı (öneri)" : "Kart üzerinden pursantaj girin"}
          </div>
        </div>
      </div>

      {/* Item cards */}
      <div>
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <h2 className="text-lg font-black text-slate-900 uppercase tracking-tight font-display">İmalat Kalemleri</h2>
          {isAdmin && (
            <Button
              className="bg-sky-500 hover:bg-sky-600 text-white font-bold h-10"
              onClick={() => setShowAddItem(true)}
              data-testid="add-item-button"
            >
              <Plus size={16} className="mr-2" /> Yeni İmalat Kalemi
            </Button>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {items.map((item) => (
            <ItemCard
              key={item.key}
              item={item}
              isAdmin={isAdmin}
              onClick={() => navigate(`/item/${item.key}`)}
              onDelete={setDeleteItem}
              onSavedPursantaj={load}
            />
          ))}
        </div>
      </div>

      {/* Comparative bar chart */}
      <div className="site-card p-6 rounded-lg" data-testid="comparative-chart">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-lg font-black text-slate-900 uppercase tracking-tight font-display">Kıyaslamalı İlerleme</h2>
            <p className="text-sm text-slate-500 mt-1">Kırmızı &lt;%30 · Turuncu %30–%75 · Yeşil ≥%75</p>
          </div>
        </div>
        <div className="w-full h-80">
          <ResponsiveContainer>
            <BarChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 40 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
              <XAxis dataKey="name" angle={-25} textAnchor="end" interval={0} tick={{ fontSize: 11, fill: "#475569" }} />
              <YAxis domain={[0, 100]} tickFormatter={(v) => `%${v}`} tick={{ fontSize: 11, fill: "#475569" }} />
              <Tooltip
                content={({ active, payload }) => {
                  if (!active || !payload?.length) return null;
                  const d = payload[0].payload;
                  return (
                    <div className="bg-white border-2 border-slate-300 rounded-md p-3 shadow-lg">
                      <div className="font-bold text-slate-900">{d.fullName}</div>
                      <div className="text-sm mt-1" style={{ color: d.color }}>%{d.progress.toFixed(1)} tamamlandı</div>
                    </div>
                  );
                }}
              />
              <Bar dataKey="progress" radius={[6, 6, 0, 0]}>
                {chartData.map((d, i) => (
                  <Cell key={i} fill={d.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <AddItemDialog open={showAddItem} onClose={() => setShowAddItem(false)} onSaved={() => { setShowAddItem(false); load(); }} />
      <ReportSettingsDialog open={showReportSettings} onClose={() => setShowReportSettings(false)} />

      <AlertDialog open={!!deleteItem} onOpenChange={(o) => !o && setDeleteItem(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>İmalat kalemini sil?</AlertDialogTitle>
            <AlertDialogDescription>
              <span className="font-bold">{deleteItem?.name}</span> ve altındaki tüm alt kırılımlar, gruplar, planlanan metrajlar ve günlük kayıtlar silinecek.
              {deleteItem && !deleteItem.is_custom && (
                <span className="block mt-2 text-slate-500 text-xs">
                  Bu bir yerleşik imalat kalemi — istediğinizde geri yükleyebilirsiniz.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>İptal</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={onDeleteItem} data-testid="confirm-delete-item">Sil</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function ReportSettingsDialog({ open, onClose }) {
  const [projectName, setProjectName] = useState("");
  const [preparedBy, setPreparedBy] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get("/settings/report")
      .then((r) => {
        setProjectName(r.data.project_name || "");
        setPreparedBy(r.data.prepared_by || "");
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [open]);

  const submit = async () => {
    if (!projectName.trim()) { toast.error("Proje adı zorunlu"); return; }
    setSaving(true);
    try {
      await api.put("/settings/report", { project_name: projectName.trim(), prepared_by: preparedBy.trim() });
      toast.success("Rapor ayarları kaydedildi");
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="report-settings-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-black uppercase tracking-tight">Rapor Ayarları</DialogTitle>
          <DialogDescription>
            Excel ve PDF raporlarının kapağında ve başlıklarında görünecek bilgiler.
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="flex justify-center py-6"><Loader2 className="animate-spin text-sky-500" size={22} /></div>
        ) : (
          <div className="space-y-3">
            <div>
              <Label htmlFor="proj-name" className="text-xs font-bold uppercase tracking-widest text-slate-600">Proje Adı *</Label>
              <Input id="proj-name" value={projectName} onChange={(e) => setProjectName(e.target.value)} placeholder="Örn: Ateş Sitesi 2. Etap"
                     className="mt-1.5 h-11 border-2" data-testid="report-project-name" maxLength={200} />
            </div>
            <div>
              <Label htmlFor="prep-by" className="text-xs font-bold uppercase tracking-widest text-slate-600">Hazırlayan (opsiyonel)</Label>
              <Input id="prep-by" value={preparedBy} onChange={(e) => setPreparedBy(e.target.value)} placeholder="Örn: Mimar Ahmet Yılmaz"
                     className="mt-1.5 h-11 border-2" data-testid="report-prepared-by" maxLength={200} />
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>İptal</Button>
          <Button className="bg-sky-500 hover:bg-sky-600 text-white font-bold" onClick={submit} disabled={saving || loading} data-testid="report-settings-save">
            {saving ? <Loader2 className="animate-spin" size={16} /> : "Kaydet"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AddItemDialog({ open, onClose, onSaved }) {
  const [name, setName] = useState("");
  const [icon, setIcon] = useState("package");
  const [color, setColor] = useState("#0ea5e9");
  const [kind, setKind] = useState("standart");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) { setName(""); setIcon("package"); setColor("#0ea5e9"); setKind("standart"); }
  }, [open]);

  const submit = async () => {
    if (!name.trim()) { toast.error("Ad zorunlu"); return; }
    setSaving(true);
    try {
      await api.post("/items", { name: name.trim(), icon, color, kind });
      toast.success("İmalat kalemi eklendi");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="add-item-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl font-black font-display">Yeni İmalat Kalemi</DialogTitle>
          <DialogDescription>
            Kaydettikten sonra kartına tıklayıp alt kırılımlarını (Standart) veya anolarını (Dere) ekleyebilirsiniz.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-5 pt-2">
          <div>
            <Label className="font-semibold text-sm text-slate-700">Ad *</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Örn: Aydınlatma Direği"
              className="mt-1.5 h-11 border-2 border-slate-300"
              autoFocus
              data-testid="add-item-name"
            />
          </div>

          <div>
            <Label className="font-semibold text-sm text-slate-700 mb-2 block">İmalat Tipi *</Label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setKind("standart")}
                className={`p-3 rounded-md border-2 text-left transition-all ${kind === "standart" ? "border-sky-500 bg-sky-50" : "border-slate-200 hover:border-slate-300"}`}
                data-testid="add-item-kind-standart"
              >
                <div className="font-black text-sm text-slate-900">📏 Standart Metraj</div>
                <div className="text-[11px] text-slate-500 mt-1 leading-tight">Alt kırılımlarla (Kazı, Beton, vb.) metraj takibi. Yol, Kanalizasyon, Boru Hattı gibi.</div>
              </button>
              <button
                type="button"
                onClick={() => setKind("dere_ano")}
                className={`p-3 rounded-md border-2 text-left transition-all ${kind === "dere_ano" ? "border-sky-500 bg-sky-50" : "border-slate-200 hover:border-slate-300"}`}
                data-testid="add-item-kind-dere"
              >
                <div className="font-black text-sm text-slate-900">💧 Dere (Ano Bazlı)</div>
                <div className="text-[11px] text-slate-500 mt-1 leading-tight">Her ano için Kazı/Radye/Perde/Tabliye aşamalarının işaretlendiği yapı.</div>
              </button>
            </div>
          </div>

          <div>
            <Label className="font-semibold text-sm text-slate-700 mb-2 block">İkon</Label>
            <div className="grid grid-cols-5 gap-2">
              {ICON_OPTIONS.map(({ key, Icon: I }) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setIcon(key)}
                  className={`h-11 flex items-center justify-center rounded-md border-2 transition-colors ${icon === key ? "border-sky-500 bg-sky-50 text-sky-700" : "border-slate-200 text-slate-500 hover:border-slate-300"}`}
                  data-testid={`add-item-icon-${key}`}
                  title={key}
                >
                  <I size={18} />
                </button>
              ))}
            </div>
          </div>

          <div>
            <Label className="font-semibold text-sm text-slate-700 mb-2 block">Renk</Label>
            <div className="flex flex-wrap gap-2">
              {COLOR_OPTIONS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setColor(c)}
                  className={`w-9 h-9 rounded-full border-2 transition-all ${color === c ? "border-slate-900 ring-2 ring-offset-2 ring-slate-900" : "border-white"}`}
                  style={{ backgroundColor: c }}
                  data-testid={`add-item-color-${c.slice(1)}`}
                  title={c}
                />
              ))}
            </div>
          </div>

          <div className="p-3 border-2 border-dashed border-slate-200 rounded-md bg-slate-50 flex items-center gap-3">
            <div className="w-10 h-10 rounded-md flex items-center justify-center text-white" style={{ backgroundColor: color }}>
              {(() => { const I = iconMap[icon] || Package; return <I size={18} />; })()}
            </div>
            <div>
              <div className="font-black text-slate-900">{name || "Ad giriniz..."}</div>
              <div className="text-xs text-slate-500">Önizleme · {kind === "dere_ano" ? "Dere (Ano Bazlı)" : "Standart Metraj"}</div>
            </div>
          </div>
        </div>
        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose}>İptal</Button>
          <Button className="bg-sky-500 hover:bg-sky-600 text-white font-bold" onClick={submit} disabled={saving} data-testid="add-item-save">
            {saving ? <Loader2 className="animate-spin" size={16} /> : "Ekle"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
